import { IPersonaProps, mergeStyles } from '@fluentui/react';
import React, { useState, useEffect } from 'react';
import searchForUsers from '../../../utils/microsoftGraphApi/userSearch';
import ErrorHandlingPeoplePicker from '../../common/formFields/ErrorHandlingPeoplePicker';
import { ICompanyCreationContact } from './types';

interface ICompanyContactsPicker {
  label?: string;
  controlName: string;
  onChange: (value: ICompanyCreationContact[]) => void;
  errorMessage?: string;
  itemLimit?: number;
  defaultValue?: ICompanyCreationContact[];
  removeButtonAriaLabel?: string;
}

const CompanyContactsPicker = ({
  errorMessage,
  onChange,
  label,
  controlName,
  itemLimit,
  defaultValue,
  removeButtonAriaLabel,
}: ICompanyContactsPicker): JSX.Element => {
  const PICKER_RESOLVE_DELAY = 450;

  // Local state to track selected items
  const [selectedItems, setSelectedItems] = useState<IPersonaProps[]>([]);

  // Initialize from defaultValue only once
  useEffect(() => {
    if (defaultValue && defaultValue.length > 0) {
      const personas = defaultValue
        .filter((user) => user?.graphId || user?.id)
        .map((user) => ({
          key: user.graphId || user.id.toString(),
          id: user.id.toString(),
          text: user.name,
          graphId: user.graphId,
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
          name: user.name,
          alias: user.alias,
          personnelNumber: user.personnelNumber,
        }));
      setSelectedItems(personas as IPersonaProps[]);
    }
  }, []);

  const onResolveSuggestions = (filterText: string): IPersonaProps[] | Promise<IPersonaProps[]> => {
    if (filterText) {
      return searchForUsers(filterText);
    }
    return [];
  };

  const handleChange = (items?: IPersonaProps[]): void => {
    setSelectedItems(items || []);
    onChange((items ?? []) as ICompanyCreationContact[]);
  };

  /** We need this style because the people picker has an absolutely positioned div that overflows panels */
  const peoplePickerWrapperStyle = mergeStyles({ position: 'relative' });

  return (
    <div role="form" className={peoplePickerWrapperStyle}>
      <ErrorHandlingPeoplePicker
        removeButtonAriaLabel={removeButtonAriaLabel}
        controlName={controlName}
        labelText={label}
        onChange={handleChange}
        selectedItems={selectedItems}
        onResolveSuggestions={onResolveSuggestions}
        resolveDelay={PICKER_RESOLVE_DELAY}
        itemLimit={itemLimit}
        errorMessage={errorMessage}
      />
    </div>
  );
};

export default CompanyContactsPicker;
